// Venue-search VS1.3: the new sections of the venue editor. Every field ID,
// label, list and rule comes from the field dictionary (src/taxonomy); the
// other words from src/content/venueEditor.js. How values are checked and
// saved: venueFacts.js.
import { useId, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { CURRENT_HOST_REQUIREMENTS } from '../../../content/hostRequirements';
import copy from '../../../content/venueEditor';
import { setFact, setFormValue } from '../../../store/hosted-venue';
import { fieldDefinition, fieldHelp, fieldLabel, listOptions, toLegacy, valueLabel } from '../../../taxonomy';
import { isShown } from '../../../taxonomy/rules';
import { colors, Field, FieldGroup, FormLabel, Input, Select } from '../../styles';
import { Checks, ErrorText, Help, Indent, Intro, Pills, SectionTitle } from './editorStyles';
import { InventorySection } from './InventoryFields';
import {
  CAPACITY_FIELDS,
  estimateFromSqft,
  FEATURE_FIELDS,
  KEPT_AMENITIES,
  m2ToSqft,
  maxGuestsFrom,
  PARKING_FIELDS,
  parkingTotal,
  parseCount,
  RESTROOM_FIELDS,
  ruleValues,
  sidesOf,
  sqftToM2
} from './venueFacts';

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const QuestionButton = styled.button`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${colors.defaultBorder};
  background: white;
  color: ${colors.defaultText};
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
`;

const Definition = styled.div`
  font-size: 13px;
  color: ${colors.fadedText};
  background-color: ${colors.lightBackground};
  border-radius: 6px;
  padding: 8px 10px;
  margin: 4px 0 6px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  padding: 10px 0;
  border-bottom: 1px solid ${colors.lightBorder};
  ${({ $error }) => $error && `border-bottom-color: ${colors.errorColor};`}

  & > fieldset {
    border: none;
    margin: 0;
    padding: 0;
    flex: 1 1 100%;
  }
`;

const Box = styled.div`
  border: 1px solid ${({ $error }) => ($error ? colors.errorColor : colors.lightBorder)};
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;

  label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 14px;
    margin-bottom: 12px;
    cursor: pointer;
  }

  input {
    margin-top: 3px;
  }

  p {
    font-size: 13px;
    margin: 0 0 12px;
  }
`;

// The "?" definitions Braeden approved for the editor (VS1.3 plan). Other
// dictionary definitions are written for bookers, so the editor leaves them out.
const EDITOR_DEFINITIONS = [
  'setting',
  'capacity.seated.indoor',
  'capacity.standing.indoor',
  'capacity.seated.outdoor',
  'capacity.standing.outdoor',
  'kitchen',
  'space.rainBackup',
  'space.danceArea',
  'space.meetingRoom',
  'rooms.bridalSuite',
  'rooms.groomsRoom',
  'access.wheelchair',
  'power.outlets',
  'power.heavyDuty',
  'rules.amplifiedMusic',
  'rules.musicEndTime',
  'rules.fire',
  'fullDay.price',
  'contact.onSite'
];

// A label, with a "?" that opens the dictionary definition under it.
export const Label = ({ id, text, htmlFor, as }) => {
  const [open, setOpen] = useState(false);
  const label = text || fieldLabel(id);
  const definition = id && EDITOR_DEFINITIONS.includes(id) && fieldDefinition(id);
  const LabelTag = as || FormLabel;
  return (
    <>
      <LabelRow>
        <LabelTag htmlFor={htmlFor}>{label}</LabelTag>
        {definition && (
          <QuestionButton type="button" aria-expanded={open} aria-label={copy.showDefinition(label)} onClick={() => setOpen(!open)}>
            ?
          </QuestionButton>
        )}
      </LabelRow>
      {open && definition && <Definition>{definition}</Definition>}
    </>
  );
};

// A 3-state Yes/No: yes, no, or not answered (blank is never no).
export const YesNo = ({ id, value, error, onChange, label }) => {
  const name = useId();
  return (
    <Row $error={error} data-invalid={!!error}>
      <fieldset>
        <Label id={label ? undefined : id} text={label} as="legend" />
        <Pills $error={error} style={{ marginTop: 8 }}>
          {[true, false].map((answer) => (
            <label key={String(answer)}>
              <input type="radio" name={name} checked={value === answer} onChange={() => onChange(answer)} />
              {answer ? copy.yes : copy.no}
            </label>
          ))}
        </Pills>
      </fieldset>
    </Row>
  );
};

const NumberField = ({ id, value, error, onChange, help, label, below }) => {
  const inputId = useId();
  return (
    <Field hasError={!!error} data-invalid={!!error}>
      <Label id={id} htmlFor={inputId} text={label} />
      <Input id={inputId} inputMode="numeric" value={value === undefined ? '' : String(value)} onChange={(e) => onChange(parseCount(e.target.value))} />
      {help && <Help>{help}</Help>}
      {below}
    </Field>
  );
};

// Half-hour steps only (Braeden, VS1.3): stored "HH:MM", shown "9:30 AM".
// The same list on every phone and computer; no seconds, no odd minutes.
export const HALF_HOURS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = i % 2 ? '30' : '00';
  const value = `${String(hours).padStart(2, '0')}:${minutes}`;
  const label = `${hours % 12 || 12}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
  return { value, label };
});

const TimeField = ({ id, value, error, onChange }) => {
  const inputId = useId();
  // An old odd time (e.g. 21:15) stays visible until the host changes it.
  const options = value && !HALF_HOURS.some((option) => option.value === value) ? [...HALF_HOURS, { value, label: value }] : HALF_HOURS;
  return (
    <Field hasError={!!error} data-invalid={!!error}>
      <Label id={id} htmlFor={inputId} />
      <Select id={inputId} value={value || ''} onChange={(e) => onChange(e.target.value || undefined)}>
        <option value=""></option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>
  );
};

const useFacts = (form) => {
  const dispatch = useDispatch();
  const values = ruleValues(form);
  const set = (id, value, estimated) => dispatch(setFact({ id, value, estimated }));
  return { values, set, dispatch };
};

// Guests, restrooms, kitchen, features, parking, rules, event types, overnight.
export const VenueFactSections = ({ form, errors }) => {
  const { values, set, dispatch } = useFacts(form);
  const yesNo = (id) => <YesNo key={id} id={id} value={values[id]} error={errors[id]} onChange={(answer) => set(id, answer)} />;
  const sides = sidesOf(values.setting);

  const onSquareFeet = (side, sqft) => {
    set(`area.${side}`, sqft === undefined ? undefined : sqftToM2(sqft));
    if (sqft === undefined) return;
    const estimate = estimateFromSqft(sqft);
    ['seated', 'standing'].forEach((kind) => {
      const id = `capacity.${kind}.${side}`;
      // Never overwrite a number the host typed.
      if (values[id] === undefined || form.estimated[id]) set(id, estimate[kind], true);
    });
  };

  const maxGuests = maxGuestsFrom(values);

  // Lights: [{kind, places}], kinds in list order.
  const lights = Array.isArray(values['lighting.details']) ? values['lighting.details'] : [];
  const lightFor = (kind) => lights.find((light) => light.kind === kind);
  const setLights = (next) => set('lighting.details', next.length ? next : undefined);
  const toggleLight = (kind, on) =>
    setLights(
      on
        ? listOptions('lightKind')
            .map(({ id }) => (id === kind ? { kind, places: [] } : lightFor(id)))
            .filter(Boolean)
        : lights.filter((light) => light.kind !== kind)
    );
  const togglePlace = (kind, place, on) =>
    setLights(
      lights.map((light) => {
        if (light.kind !== kind) return light;
        const places = on ? [...light.places, place] : light.places.filter((p) => p !== place);
        return {
          kind,
          places: listOptions('lightPlace')
            .map(({ id }) => id)
            .filter((id) => places.includes(id))
        };
      })
    );

  // Event types not allowed: [] = none; {list, other} when Other is picked.
  const restricted = values['rules.eventTypesNotAllowed'];
  const restrictedIds = Array.isArray(restricted) ? restricted : restricted?.list || [];
  const otherText = restricted?.other || '';
  const setRestricted = (ids, other = otherText) =>
    set('rules.eventTypesNotAllowed', !ids.length ? undefined : ids.includes('other') ? { list: ids, other } : ids);

  const keptLabels = form.amenities || [];
  const toggleAmenity = (label, on) => dispatch(setFormValue({ amenities: on ? [...keptLabels, label] : keptLabels.filter((value) => value !== label) }));

  const beds = values['overnight.beds'] || {};

  return (
    <>
      <SectionTitle>{copy.guests.title}</SectionTitle>
      {sides.length > 0 && (
        <>
          <FormLabel>{copy.guests.squareFootage}</FormLabel>
          <FieldGroup>
            {sides.map((side) => {
              const id = `area.${side}`;
              return (
                <NumberField
                  key={id}
                  id={id}
                  label={fieldLabel(id)}
                  value={values[id] === undefined ? undefined : m2ToSqft(values[id])}
                  error={errors[id]}
                  onChange={(sqft) => onSquareFeet(side, sqft)}
                />
              );
            })}
          </FieldGroup>
          <Help style={{ margin: '-8px 0 12px' }}>{copy.guests.squareFootageHelp}</Help>
        </>
      )}
      <FieldGroup style={{ flexWrap: 'wrap' }}>
        {CAPACITY_FIELDS.filter((id) => isShown(id, values)).map((id) => (
          <NumberField
            key={id}
            id={id}
            value={values[id]}
            error={errors[id]}
            onChange={(n) => set(id, n)}
            help={form.estimated[id] ? fieldHelp('area.indoor') : undefined}
          />
        ))}
      </FieldGroup>
      {maxGuests && <Help style={{ marginBottom: 8 }}>{copy.guests.maxGuests(maxGuests)}</Help>}

      <SectionTitle>{copy.restrooms}</SectionTitle>
      <FieldGroup>
        {RESTROOM_FIELDS.map((id) => (
          <NumberField key={id} id={id} value={values[id]} error={errors[id]} onChange={(n) => set(id, n)} />
        ))}
      </FieldGroup>
      <Help style={{ margin: '-8px 0 8px' }}>{fieldHelp('restrooms.womens')}</Help>

      <SectionTitle>{copy.kitchen}</SectionTitle>
      <Row $error={errors.kitchen} data-invalid={!!errors.kitchen}>
        <fieldset>
          <Label id="kitchen" as="legend" />
          <Pills $error={errors.kitchen} style={{ marginTop: 8, flexWrap: 'wrap' }}>
            {listOptions('kitchen').map(({ id, label }) => (
              <label key={id}>
                <input type="radio" name="kitchen" checked={values.kitchen === id} onChange={() => set('kitchen', id)} />
                {label}
              </label>
            ))}
          </Pills>
        </fieldset>
      </Row>

      <SectionTitle>{copy.features.title}</SectionTitle>
      <Intro>{copy.features.intro}</Intro>
      {FEATURE_FIELDS.map(yesNo)}
      {isShown('lighting.details', values) && (
        <Indent>
          <FormLabel>{copy.lights.which}</FormLabel>
          <Checks>
            {listOptions('lightKind').map(({ id, label }) => (
              <label key={id}>
                <input type="checkbox" checked={!!lightFor(id)} onChange={(e) => toggleLight(id, e.target.checked)} />
                {label}
              </label>
            ))}
          </Checks>
          {lights.map((light) => (
            <Indent key={light.kind}>
              <FormLabel>
                {valueLabel('lightKind', light.kind)}. {copy.lights.where}
              </FormLabel>
              <Checks>
                {listOptions('lightPlace').map(({ id, label }) => (
                  <label key={id}>
                    <input type="checkbox" checked={light.places.includes(id)} onChange={(e) => togglePlace(light.kind, id, e.target.checked)} />
                    {label}
                  </label>
                ))}
              </Checks>
            </Indent>
          ))}
        </Indent>
      )}

      <InventorySection form={form} errors={errors} />

      <SectionTitle>{copy.alsoAtVenue}</SectionTitle>
      <Checks>
        {KEPT_AMENITIES.map((id) => {
          const legacy = toLegacy('amenity', id);
          return (
            <label key={id}>
              <input type="checkbox" checked={keptLabels.includes(legacy)} onChange={(e) => toggleAmenity(legacy, e.target.checked)} />
              {valueLabel('amenity', id)}
            </label>
          );
        })}
      </Checks>

      <SectionTitle>{copy.parking.title}</SectionTitle>
      <Intro>{copy.parking.intro}</Intro>
      <FieldGroup>
        {PARKING_FIELDS.map((id) => (
          <NumberField key={id} id={id} value={values[id]} error={errors[id]} onChange={(n) => set(id, n)} />
        ))}
      </FieldGroup>
      {PARKING_FIELDS.some((id) => values[id] !== undefined) && <Help style={{ margin: '-8px 0 8px' }}>{copy.parking.total(parkingTotal(values))}</Help>}
      {isShown('parking.shuttle', values) && yesNo('parking.shuttle')}

      <SectionTitle>{copy.rules}</SectionTitle>
      {yesNo('rules.alcohol')}
      {yesNo('rules.amplifiedMusic')}
      {isShown('rules.musicEndTime', values) && (
        <Indent>
          <TimeField
            id="rules.musicEndTime"
            value={values['rules.musicEndTime']}
            error={errors['rules.musicEndTime']}
            onChange={(t) => set('rules.musicEndTime', t)}
          />
        </Indent>
      )}
      {yesNo('rules.dogs')}
      {yesNo('rules.fire')}

      <SectionTitle>{copy.eventTypes.title}</SectionTitle>
      <YesNo
        id="rules.eventTypesNotAllowed"
        label={copy.eventTypes.question}
        value={form.eventTypesRestricted}
        error={errors['rules.eventTypesNotAllowed'] === 'missing'}
        onChange={(answer) => {
          dispatch(setFormValue({ eventTypesRestricted: answer }));
          set('rules.eventTypesNotAllowed', answer ? undefined : []);
        }}
      />
      {form.eventTypesRestricted === true && (
        <Indent>
          <FormLabel>{copy.eventTypes.which}</FormLabel>
          <Checks>
            {listOptions('eventType').map(({ id, label }) => (
              <label key={id}>
                <input
                  type="checkbox"
                  checked={restrictedIds.includes(id)}
                  onChange={(e) =>
                    setRestricted(
                      listOptions('eventType')
                        .map((option) => option.id)
                        .filter((value) => (value === id ? e.target.checked : restrictedIds.includes(value)))
                    )
                  }
                />
                {label}
              </label>
            ))}
          </Checks>
          {errors['rules.eventTypesNotAllowed'] === 'pickOne' && <ErrorText data-invalid="true">{copy.eventTypes.pickOne}</ErrorText>}
          {restrictedIds.includes('other') && (
            <Field hasError={!!errors['rules.eventTypesNotAllowed.other']}>
              <FormLabel>{copy.eventTypes.otherText}</FormLabel>
              <Input maxLength={100} value={otherText} onChange={(e) => setRestricted(restrictedIds, e.target.value)} />
            </Field>
          )}
        </Indent>
      )}

      <SectionTitle>{copy.overnight.title}</SectionTitle>
      <YesNo
        id="overnight.allowed"
        label={copy.overnight.question}
        value={values['overnight.allowed']}
        error={errors['overnight.allowed']}
        onChange={(answer) => set('overnight.allowed', answer)}
      />
      {isShown('overnight.beds', values) && (
        <Indent>
          <Label id="overnight.beds" />
          <FieldGroup style={{ flexWrap: 'wrap' }}>
            {listOptions('bedType').map(({ id, label }) => (
              <NumberField
                key={id}
                label={label}
                value={beds[id]}
                onChange={(n) => {
                  const next = { ...beds };
                  if (n === undefined) delete next[id];
                  else next[id] = n;
                  set('overnight.beds', Object.keys(next).length ? next : undefined);
                }}
              />
            ))}
          </FieldGroup>
          {errors['overnight.beds'] && <ErrorText data-invalid="true">{copy.overnight.noBeds}</ErrorText>}
          <NumberField
            id="overnight.showers"
            value={values['overnight.showers']}
            error={errors['overnight.showers']}
            onChange={(n) => set('overnight.showers', n)}
          />
        </Indent>
      )}
    </>
  );
};

// Pricing: the hours the day price includes, and the optional hourly price.
export const DayHours = ({ form, errors }) => {
  const { values, set } = useFacts(form);
  const endProblem = errors['fullDay.end'];
  return (
    <>
      <FormLabel>{copy.pricing.hoursIncluded}</FormLabel>
      <FieldGroup>
        <TimeField id="fullDay.start" value={values['fullDay.start']} error={errors['fullDay.start']} onChange={(t) => set('fullDay.start', t)} />
        <TimeField id="fullDay.end" value={values['fullDay.end']} error={endProblem} onChange={(t) => set('fullDay.end', t)} />
      </FieldGroup>
      <Help style={{ margin: '-8px 0 8px' }}>{fieldHelp('fullDay.start')}</Help>
      {endProblem === 'endBeforeStart' && <ErrorText>{copy.pricing.endBeforeStart}</ErrorText>}
      {endProblem === 'tooShort' && <ErrorText>{copy.pricing.tooShort}</ErrorText>}
    </>
  );
};

export const HourlyPrice = ({ form, errors, currency }) => {
  const { values, set } = useFacts(form);
  const price = values['hourly.price'];
  return (
    <FieldGroup>
      <NumberField
        id="hourly.price"
        value={price ? price.cents / 100 : undefined}
        error={errors['hourly.price']}
        help={fieldHelp('hourly.price')}
        onChange={(dollars) => set('hourly.price', dollars === undefined ? undefined : { cents: dollars * 100, currency })}
      />
      {isShown('hourly.minHours', values) && (
        <NumberField id="hourly.minHours" value={values['hourly.minHours']} error={errors['hourly.minHours']} onChange={(n) => set('hourly.minHours', n)} />
      )}
    </FieldGroup>
  );
};

export const OnSiteContact = ({ form, errors }) => {
  const dispatch = useDispatch();
  const nameId = useId();
  const phoneId = useId();
  const problem = errors['contact.onSite'];
  return (
    <>
      <SectionTitle as="div">
        <Label id="contact.onSite" as="span" />
      </SectionTitle>
      <FieldGroup>
        <Field hasError={problem === 'missing' && !String(form.contactName).trim()} data-invalid={!!problem}>
          <FormLabel htmlFor={nameId}>{copy.contact.name}</FormLabel>
          <Input
            id={nameId}
            autoComplete="off"
            maxLength={100}
            value={form.contactName}
            onChange={(e) => dispatch(setFormValue({ contactName: e.target.value }))}
          />
        </Field>
        <Field hasError={!!problem && (problem === 'badPhone' || !String(form.contactPhone).trim())}>
          <FormLabel htmlFor={phoneId}>{copy.contact.phone}</FormLabel>
          <Input
            id={phoneId}
            type="tel"
            autoComplete="off"
            value={form.contactPhone}
            onChange={(e) => dispatch(setFormValue({ contactPhone: e.target.value }))}
          />
          {problem === 'badPhone' && <ErrorText>{copy.contact.badPhone}</ErrorText>}
        </Field>
      </FieldGroup>
    </>
  );
};

// Shown only when this venue has no saved confirmation and none is in this tab.
export const RequirementsBox = ({ checked, onChange, error }) => {
  const { statements } = CURRENT_HOST_REQUIREMENTS;
  return (
    <Box $error={error} data-invalid={!!error}>
      <SectionTitle style={{ marginTop: 0 }}>{copy.requirements.title}</SectionTitle>
      <p>{copy.requirements.intro}</p>
      {statements.map(({ id, text }) => (
        <label key={id}>
          <input type="checkbox" checked={!!checked[id]} onChange={(e) => onChange({ ...checked, [id]: e.target.checked })} />
          <span>{text}</span>
        </label>
      ))}
      {error && <ErrorText style={{ marginBottom: 8 }}>{copy.requirements.error}</ErrorText>}
      <Help>
        {copy.requirements.questions} <a href={`mailto:${copy.requirements.email}`}>{copy.requirements.email}</a>.
      </Help>
    </Box>
  );
};
